import React, { createContext, useContext, useReducer, useCallback, useMemo } from 'react';
import { useLocalStorageState } from '../hooks/useLocalStorageState';
import type {
  DataEntry,
  DateRange,
  GeneralLedgerFilters,
  TaskStatusFilter,
  LedgerAccount,
  SidebarDescriptor,
  GeneralLedgerListItem,
} from '../types';
import sidebarTypes from '../sidebarTypes';

export type SortState = {
  key: string;
  direction: 'asc' | 'desc';
};

export type GeneralLedgerContextState = {
  filters: GeneralLedgerFilters;
  dateRange: DateRange;
  isNavigationExpanded: boolean;
  selectedDataEntry?: DataEntry;
  selectedLedgerAccount?: LedgerAccount;
  sidebarDescriptor?: SidebarDescriptor;
  sortStates: Record<string, SortState>;
  hiddenCategories: string[];
  showAccounts: boolean;
  showQualityIssues: boolean;
  taskStatusFilter: TaskStatusFilter;
  search: string;
  filterMito: boolean;
  firstVisibleIndex?: number;
  listHeight?: number;
  showSourceColumn: boolean;
};

export type GeneralLedgerAction =
  | { type: 'UPDATE_FILTERS'; payload: Partial<GeneralLedgerFilters> }
  | { type: 'SET_DATE_RANGE'; payload: DateRange }
  | { type: 'SET_HIDDEN_CATEGORIES'; payload: string[] }
  | { type: 'SET_SELECTED_DATA_ENTRY'; payload: DataEntry | undefined }
  | { type: 'SET_SELECTED_LEDGER_ACCOUNT'; payload: LedgerAccount | undefined }
  | { type: 'SET_SIDEBAR_DESCRIPTOR'; payload: SidebarDescriptor | undefined }
  | { type: 'CLEAR_SIDEBAR' }
  | { type: 'TOGGLE_NAVIGATION' }
  | { type: 'SET_SORT_STATE'; payload: { ledgerAccountId: string; sortState: SortState } }
  | { type: 'SET_FIRST_VISIBLE_INDEX'; payload: number | undefined }
  | { type: 'SET_LIST_HEIGHT'; payload: number | undefined }
  | { type: 'RESET_STATE'; payload?: Partial<GeneralLedgerContextState> }
  | { type: 'SET_FILTER_MITO' }
  | { type: 'TOGGLE_SOURCE_COLUMN' };

export type GeneralLedgerContextType = GeneralLedgerContextState & {
  updateFilters: (filters: Partial<GeneralLedgerFilters>) => void;
  updateDateRange: (dateRange: DateRange) => void;
  updateHiddenCategories: (categories: string[]) => void;
  updateSelectedDataEntry: (dataEntry: DataEntry | undefined) => void;
  updateSelectedLedgerAccount: (ledgerAccount: LedgerAccount | undefined) => void;
  updateSidebarDescriptor: (descriptor: SidebarDescriptor | undefined) => void;
  clearSidebar: () => void;
  toggleNavigation: () => void;
  updateSortState: (ledgerAccountId: string, sortState: SortState) => void;
  updateFirstVisibleIndex: (index: number | undefined) => void;
  updateListHeight: (height: number | undefined) => void;
  resetState: (newState?: Partial<GeneralLedgerContextState>) => void;
  updateFilterMito: () => void;
  toggleSourceColumn: () => void;
  itemData?: Array<GeneralLedgerListItem>;
  scrollToItem?: (index: number) => void;
  setItemData: (itemData: Array<GeneralLedgerListItem>) => void;
  setScrollToItem: (scrollToItem: (index: number) => void) => void;
};

const defaultInitialState: GeneralLedgerContextState = {
  filters: {
    search: '',
    showAccounts: false,
    showQualityIssues: false,
    taskStatusFilter: 'none',
  },
  dateRange: {
    startDate: undefined,
    endDate: undefined,
  },
  isNavigationExpanded: true,
  selectedDataEntry: undefined,
  selectedLedgerAccount: undefined,
  sidebarDescriptor: undefined,
  sortStates: {},
  hiddenCategories: [],
  showAccounts: false,
  showQualityIssues: false,
  taskStatusFilter: 'none',
  filterMito: false,
  search: '',
  firstVisibleIndex: undefined,
  listHeight: undefined,
  showSourceColumn: false,
};

const GeneralLedgerContext = createContext<GeneralLedgerContextType | undefined>(undefined);

function generalLedgerReducer(
  state: GeneralLedgerContextState,
  action: GeneralLedgerAction,
): GeneralLedgerContextState {
  switch (action.type) {
    case 'UPDATE_FILTERS':
      return {
        ...state,
        filters: { ...state.filters, ...action.payload },
        showAccounts: action.payload.showAccounts ?? state.showAccounts,
        showQualityIssues: action.payload.showQualityIssues ?? state.showQualityIssues,
        taskStatusFilter: action.payload.taskStatusFilter ?? state.taskStatusFilter,
        search: action.payload.search ?? state.search,
      };

    case 'SET_FILTER_MITO':
      return {
        ...state,
        filterMito: !state.filterMito,
      };

    case 'SET_DATE_RANGE':
      return {
        ...state,
        dateRange: action.payload,
      };

    case 'SET_HIDDEN_CATEGORIES':
      return {
        ...state,
        hiddenCategories: action.payload,
      };

    case 'SET_SELECTED_DATA_ENTRY':
      return {
        ...state,
        selectedDataEntry: action.payload,
      };

    case 'SET_SELECTED_LEDGER_ACCOUNT':
      const isSameAccount = state.selectedLedgerAccount?.id === action.payload?.id;
      return {
        ...state,
        selectedLedgerAccount: isSameAccount ? undefined : action.payload,
        sidebarDescriptor: isSameAccount
          ? undefined
          : action.payload
          ? {
              sidebarType: sidebarTypes.LEDGER_ACCOUNT as keyof typeof sidebarTypes,
              sidebarElementId: action.payload.id,
            }
          : state.sidebarDescriptor,
      };

    case 'SET_SIDEBAR_DESCRIPTOR':
      return {
        ...state,
        sidebarDescriptor: action.payload,
      };

    case 'CLEAR_SIDEBAR':
      return {
        ...state,
        sidebarDescriptor: undefined,
        selectedLedgerAccount: undefined,
        selectedDataEntry: undefined,
      };

    case 'TOGGLE_NAVIGATION':
      return {
        ...state,
        isNavigationExpanded: !state.isNavigationExpanded,
      };

    case 'SET_SORT_STATE':
      return {
        ...state,
        sortStates: {
          ...state.sortStates,
          [action.payload.ledgerAccountId]: action.payload.sortState,
        },
      };

    case 'SET_FIRST_VISIBLE_INDEX':
      return {
        ...state,
        firstVisibleIndex: action.payload,
      };

    case 'SET_LIST_HEIGHT':
      return {
        ...state,
        listHeight: action.payload,
      };

    case 'RESET_STATE':
      return {
        ...defaultInitialState,
        ...action.payload,
      };

    case 'TOGGLE_SOURCE_COLUMN':
      return {
        ...state,
        showSourceColumn: !state.showSourceColumn,
      };

    default:
      return state;
  }
}

export type GeneralLedgerProviderProps = {
  children: React.ReactNode;
  initialState?: Partial<GeneralLedgerContextState>;
  clientId: string;
  onFiltersChange?: (filters: GeneralLedgerFilters) => void;
};

export const GeneralLedgerProvider: React.FC<GeneralLedgerProviderProps> = ({
  children,
  initialState,
  clientId,
  onFiltersChange,
}) => {
  const [storedHiddenCategories, setStoredHiddenCategories] = useLocalStorageState<string[]>(
    `GeneralLedger:HiddenCategories:${clientId}`,
    [],
  );

  const [storedShowSourceColumn, setStoredShowSourceColumn] = useLocalStorageState<boolean>(
    `GeneralLedger:ShowSourceColumn:${clientId}`,
    false,
  );

  const [state, dispatch] = useReducer(generalLedgerReducer, {
    ...defaultInitialState,
    ...initialState,
    hiddenCategories: [...storedHiddenCategories, ...(initialState?.hiddenCategories || [])],
    showSourceColumn: initialState?.showSourceColumn ?? storedShowSourceColumn,
    filters: {
      ...defaultInitialState.filters,
      ...initialState?.filters,
    },
  });

  const [itemData, setItemDataState] = React.useState<Array<GeneralLedgerListItem>>([]);
  const [scrollToItem, setScrollToItemState] = React.useState<
    ((index: number) => void) | undefined
  >();

  const updateFilters = useCallback(
    (filters: Partial<GeneralLedgerFilters>) => {
      dispatch({ type: 'UPDATE_FILTERS', payload: filters });

      if (onFiltersChange) {
        onFiltersChange({ ...state.filters, ...filters });
      }
    },
    [state.filters, onFiltersChange],
  );

  const updateDateRange = useCallback(
    (dateRange: DateRange) => {
      dispatch({ type: 'SET_DATE_RANGE', payload: dateRange });

      if (onFiltersChange) {
        onFiltersChange({ ...state.filters, ...dateRange });
      }
    },
    [onFiltersChange, state.filters],
  );

  const updateHiddenCategories = useCallback(
    (categories: string[]) => {
      setStoredHiddenCategories(categories);
      dispatch({ type: 'SET_HIDDEN_CATEGORIES', payload: categories });
    },
    [setStoredHiddenCategories],
  );

  const updateSelectedDataEntry = useCallback((dataEntry: DataEntry | undefined) => {
    dispatch({ type: 'SET_SELECTED_DATA_ENTRY', payload: dataEntry });
  }, []);

  const updateSelectedLedgerAccount = useCallback((ledgerAccount: LedgerAccount | undefined) => {
    dispatch({ type: 'SET_SELECTED_LEDGER_ACCOUNT', payload: ledgerAccount });
  }, []);

  const updateSidebarDescriptor = useCallback((descriptor: SidebarDescriptor | undefined) => {
    dispatch({ type: 'SET_SIDEBAR_DESCRIPTOR', payload: descriptor });
  }, []);

  const clearSidebar = useCallback(() => {
    dispatch({ type: 'CLEAR_SIDEBAR' });
  }, []);

  const toggleNavigation = useCallback(() => {
    dispatch({ type: 'TOGGLE_NAVIGATION' });
  }, []);

  const updateSortState = useCallback((ledgerAccountId: string, sortState: SortState) => {
    dispatch({ type: 'SET_SORT_STATE', payload: { ledgerAccountId, sortState } });
  }, []);

  const updateFilterMito = useCallback(() => {
    dispatch({ type: 'SET_FILTER_MITO' });
  }, []);

  const toggleSourceColumn = useCallback(() => {
    const newValue = !state.showSourceColumn;
    setStoredShowSourceColumn(newValue);
    dispatch({ type: 'TOGGLE_SOURCE_COLUMN' });
  }, [state.showSourceColumn, setStoredShowSourceColumn]);

  const updateFirstVisibleIndex = useCallback((index: number | undefined) => {
    dispatch({ type: 'SET_FIRST_VISIBLE_INDEX', payload: index });
  }, []);

  const updateListHeight = useCallback((height: number | undefined) => {
    dispatch({ type: 'SET_LIST_HEIGHT', payload: height });
  }, []);

  const resetState = useCallback((newState?: Partial<GeneralLedgerContextState>) => {
    dispatch({ type: 'RESET_STATE', payload: newState });
  }, []);

  const setItemData = useCallback((newItemData: Array<GeneralLedgerListItem>) => {
    setItemDataState(newItemData);
  }, []);

  const setScrollToItem = useCallback((newScrollToItem: (index: number) => void) => {
    setScrollToItemState(() => newScrollToItem);
  }, []);

  const contextValue = useMemo(
    () => ({
      ...state,
      updateFilters,
      updateDateRange,
      updateHiddenCategories,
      updateSelectedDataEntry,
      updateSelectedLedgerAccount,
      updateSidebarDescriptor,
      clearSidebar,
      toggleNavigation,
      updateSortState,
      updateFilterMito,
      toggleSourceColumn,
      updateFirstVisibleIndex,
      updateListHeight,
      resetState,
      itemData,
      scrollToItem,
      setItemData,
      setScrollToItem,
    }),
    [
      state,
      updateFilters,
      updateDateRange,
      updateHiddenCategories,
      updateSelectedDataEntry,
      updateSelectedLedgerAccount,
      updateSidebarDescriptor,
      clearSidebar,
      toggleNavigation,
      updateSortState,
      updateFilterMito,
      toggleSourceColumn,
      updateFirstVisibleIndex,
      updateListHeight,
      resetState,
      itemData,
      scrollToItem,
      setItemData,
      setScrollToItem,
    ],
  );

  return (
    <GeneralLedgerContext.Provider value={contextValue}>{children}</GeneralLedgerContext.Provider>
  );
};

export const useGeneralLedger = () => {
  const context = useContext(GeneralLedgerContext);
  if (!context) {
    throw new Error('useGeneralLedger must be used within a GeneralLedgerProvider');
  }
  return context;
};
